<template>
  <div class="login-container">
    <RegLeftBar/>
    <div class="form-section">
      <div class="login-form">
        <h1 class="title">Log in to Giftxyz</h1>
        <div class="form-input">
          <base-input
              v-model="form.login"
              :input-type="'text'"
              :input-name="'login'"
              :label-name="'Username or Email Address'"
              :label-class="'mb-12'"
              :blue="true"
          />
        </div>
        <div class="form-input password">
          <base-input
              v-model="form.password"
              :input-type="'password'"
              :input-name="'password'"
              :label-name="'Password'"
              :label-class="'mb-12'"
              :input-link="'Forgot password?'"
              :input-link-name="'forget-password'"
              :blue="true"
          />
        </div>
        <div class="checkbox">
          <input type="checkbox" id="checkbox" v-model="form.remember">
          <label for="checkbox">Remember Me</label>
        </div>

        <base-button
            class="primary register-btn"
            @click.prevent="login"
        >
          Log in
        </base-button>
        <div v-if="needsVerification" class="verification-notice" role="status">
          <p>{{ verificationMessage || 'Check your inbox to verify your email before logging in.' }}</p>
          <button type="button" class="primary" :disabled="resending || resendSeconds > 0" @click="resendVerification">
            {{ resending ? 'Sending…' : resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend verification email' }}
          </button>
        </div>
        <div class="register-link">
          Not a member?
          <router-link class="link" :to="{name: 'register'}"> Register now.</router-link>
        </div>
        <button class="primary x" @click="xLogin">
          <XIcon/>
          <span> Sign in With X</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import './LoginPage.scss'
import RegLeftBar from '@/router/components/RegLeftBar/RegLeftBar.vue';
import BaseInput from "../../UI/BaseInput";
import BaseButton from "../../UI/BaseButton";
import XIcon from '../../../assets/svg/XIcon.vue';
import {useRouter} from "vue-router";

// eslint-disable-next-line no-unused-vars
const router = useRouter()

import {useForm} from 'vee-validate';
import {object, string} from 'yup';

useForm({
  validationSchema: object({
    login: string().required(),
    password: string().required().min(8),
  }),
})
</script>

<script>
import {mapActions} from "vuex";
import preloader from "@/mixins/preloader";
import {xOAuthUrl} from "@/helpers/xOAuth";
import {ensureCsrfCookie} from "@/packages/axios";

export default {
  name: "LoginPage",
  mixins: [preloader],
  data() {
    return {
      needsVerification: false,
      verificationMessage: '',
      resending: false,
      resendSeconds: 0,
      resendTimer: null,
      form: {
        login: null,
        password: null,
        remember: false
      }
    }
  },
  async mounted() {
    this.setLoading(false);

    const socialError = this.$route.query.social_error;
    if (socialError) {
      this.toast.error(socialError);
      const query = { ...this.$route.query };
      delete query.social_error;
      this.$router.replace({ query });
    }

    const oauthCode = this.$route.query.oauth_code;
    if (oauthCode) {
      try {
        const { data } = await this.axios.post('oauth/x/token', { code: oauthCode });
        localStorage.setItem('access_token', data.token);
        this.axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        await this.$router.replace({ name: 'login' });
        await this.signIn({ force: true });
        const user = this.$store.getters['auth/user'];
        await this.$router.replace(user.role_id && user.location_id && user.selected_currency_id ? '/dashboard' : '/x');
      } catch (error) {
        await this.$router.replace({ name: 'login' });
        this.toast.error(error.response?.data?.message || 'X sign-in code is invalid or expired. Please try again.');
      }
    }
  },
  beforeUnmount() {
    clearInterval(this.resendTimer);
  },
  methods: {
    async resendVerification() {
      if (this.resending || this.resendSeconds > 0) return;
      this.resending = true;
      try {
        await ensureCsrfCookie();
        const { data } = await this.axios.post('resend-verification', {
          login: this.form.login, password: this.form.password
        });
        this.verificationMessage = data.message;
        this.startResendCooldown(data.retry_after || 0);
      } catch (error) {
        this.verificationMessage = error.response?.data?.message || 'Unable to resend. Please try again.';
        this.startResendCooldown(error.response?.data?.retry_after || (error.response?.status === 429 ? 60 : 0));
      } finally {
        this.resending = false;
      }
    },
    startResendCooldown(seconds) {
      clearInterval(this.resendTimer);
      this.resendSeconds = seconds;
      if (!seconds) return;
      this.resendTimer = setInterval(() => {
        this.resendSeconds = Math.max(0, this.resendSeconds - 1);
        if (!this.resendSeconds) clearInterval(this.resendTimer);
      }, 1000);
    },
    ...mapActions({
      signIn: 'auth/login',
      getCurrency: "getCurrency"
    }),
    login() {
      this.setLoading(true);
      ensureCsrfCookie().then(() => this.axios.post('login', {
        login: this.form.login,
        password: this.form.password,
        remember: this.form.remember
      })).then((res) => {
        const token = res.data.data.token
        localStorage.setItem('access_token', token)
        this.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        return this.signIn().then(() => {
          this.toast.info(res.data.message)
          this.form.login = null
          this.form.password = null
          this.getCurrency()
          this.$router.push('/dashboard')
        })
      }).catch((error) => {
        this.needsVerification = error.response?.data?.code === 'EMAIL_NOT_VERIFIED';
        this.toast.error(error?.response?.data?.message || 'Login failed')
        this.setLoading(false);
      })
    },
    xLogin() {
      window.location.href = xOAuthUrl()
    }
  },
}

</script>

<style scoped>
.verification-notice { margin-top: 20px; padding: 16px; border-radius: 12px; background: #eef8fc; color: #102e55; }
.verification-notice p { margin: 0 0 12px; line-height: 1.5; }
.verification-notice button { width: 100%; padding: 14px; border: 0; border-radius: 8px; cursor: pointer; }
.verification-notice button:disabled { opacity: 0.6; cursor: default; }
</style>
